import * as React from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";


export interface CascadingOption {
  label: string;
  value: string;
  children?: CascadingOption[];
}

interface CascadingSelectProps {
  options: CascadingOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  className?: string;
  /** Extra class for the popover panel — used to scope-override accent colors (see FreePracticeDialog). */
  popoverClassName?: string;
}

export function CascadingSelect({
  options,
  value,
  onChange,
  placeholder = "Select options",
  className,
  popoverClassName,
}: CascadingSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [expandedParents, setExpandedParents] = React.useState<Set<string>>(
    new Set()
  );

  // 获取选中项的显示标签
  const getSelectedLabels = () => {
    const labels: string[] = [];
    options.forEach((parent) => {
      if (parent.children) {
        parent.children.forEach((child) => {
          if (value.includes(child.value)) {
            labels.push(`${parent.label} > ${child.label}`);
          }
        });
      }
    });
    return labels;
  };

  const selectedLabels = getSelectedLabels();

  // 切换父级展开/收起
  const toggleParent = (parentValue: string) => {
    const newExpanded = new Set(expandedParents);
    if (newExpanded.has(parentValue)) {
      newExpanded.delete(parentValue);
    } else {
      newExpanded.add(parentValue);
    }
    setExpandedParents(newExpanded);
  };

  // 选择/取消选择子项
  const toggleChild = (childValue: string) => {
    const newValue = value.includes(childValue)
      ? value.filter((v) => v !== childValue)
      : [...value, childValue];
    onChange(newValue);
  };



  // 清空所有选择
  const clearAll = () => {
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between min-h-10 h-auto flex bg-muted hover:bg-muted",
            className
          )}
        >
          <div className="flex gap-1 flex-wrap flex-1 min-w-0">
            {selectedLabels.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              // 只显示选中项的数量
              <span className="text-sm">
                {selectedLabels.length} items selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {selectedLabels.length > 0 && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  clearAll();
                }}
                className="hover:bg-accent rounded-sm p-1"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-full p-0", popoverClassName)} align="start">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((parent) => (
                <div key={parent.value}>
                  {/* 父级项 */}
                  <CommandItem
                    onSelect={() => toggleParent(parent.value)}
                    className="cursor-pointer font-medium"
                  >
                    <ChevronDown
                      className={cn(
                        "mr-2 h-4 w-4 transition-transform",
                        expandedParents.has(parent.value) ? "" : "-rotate-90"
                      )}
                    />
                    {parent.label}
                  </CommandItem>

                  {/* 子级项 */}
                  {expandedParents.has(parent.value) &&
                    parent.children?.map((child) => (
                      <CommandItem
                        key={child.value}
                        onSelect={() => toggleChild(child.value)}
                        className="cursor-pointer pl-8"
                      >
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            value.includes(child.value)
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible"
                          )}
                        >
                          <Check className="h-4 w-4" />
                        </div>
                        {child.label}
                      </CommandItem>
                    ))}
                </div>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

